import { memo, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useFetch } from "../../../hooks/useFetch";

// VITARI/Cujo run-log viewer (SW-26430).
//
// Reads the parsed run-log data the telemetry-backend ingestion handler writes
// to the snoopdb_analytics Athena tables, via the new read-only endpoint
// GET /api/run/{id}/runLog. There is a single source of truth per table: if a
// run has no rows we say so, we never fall back to another source.

type RunLogTable = "run_log" | "values_log" | "slow_values_log";

type RunLogResponse = {
  table: string;
  runId: string;
  runDate: string;
  runType: string;
  columns: string[];
  rows: Array<Record<string, string | number | null>>;
  rowCount: number;
};

type RunAthenaLogsProps = {
  runID: string;
  runDate?: Date | string;
  runType?: string;
};

const SUB_VIEWS: { key: RunLogTable; label: string }[] = [
  { key: "run_log", label: "run.log" },
  { key: "values_log", label: "Values" },
  { key: "slow_values_log", label: "Slow Values" },
];

// toYyyyMmDd renders a Date (or date-ish string) as the YYYY-MM-DD partition key
// Athena expects. Returns "" when the input is missing/unparseable so the caller
// can surface an explicit "date unavailable" state instead of querying blindly.
const toYyyyMmDd = (d?: Date | string): string => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const RunAthenaLogs = memo(
  ({ runID, runDate, runType }: RunAthenaLogsProps) => {
    const [table, setTable] = useState<RunLogTable>("run_log");
    const [filterText, setFilterText] = useState("");
    const [appliedFilter, setAppliedFilter] = useState("");

    const dateStr = useMemo(() => toYyyyMmDd(runDate), [runDate]);
    const canQuery = !!runID && !!dateStr;

    const url = useMemo(() => {
      const params = new URLSearchParams({
        table,
        limit: "500",
      });
      if (dateStr) params.set("runDate", dateStr);
      // run_type is an enum partition; omit it so all types are scanned for the
      // date (a Run object doesn't carry the Athena run_type). Only send it if a
      // caller explicitly narrows to one.
      if (runType) params.set("runType", runType);
      if (appliedFilter) {
        params.set(table === "run_log" ? "search" : "register", appliedFilter);
      }
      return `/run/${runID}/runLog?${params.toString()}`;
    }, [runID, table, runType, dateStr, appliedFilter]);

    const { data, isLoading, isError, error } = useFetch(url, canQuery);
    const result = data as RunLogResponse | undefined;

    const columns = result?.columns ?? [];
    const rows = result?.rows ?? [];

    const applyFilter = () => setAppliedFilter(filterText.trim());

    const changeTable = (next: RunLogTable) => {
      setTable(next);
      setFilterText("");
      setAppliedFilter("");
    };

    return (
      <Box
        data-testid="run-athena-logs"
        sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Tabs
            value={table}
            onChange={(_, v: RunLogTable) => changeTable(v)}
            data-testid="run-athena-logs-subtabs"
          >
            {SUB_VIEWS.map((v) => (
              <Tab
                key={v.key}
                label={v.label}
                value={v.key}
                data-testid={`run-athena-logs-tab-${v.key}`}
              />
            ))}
          </Tabs>

          <TextField
            size="small"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilter();
            }}
            onBlur={applyFilter}
            placeholder={
              table === "run_log" ? "Search message…" : "Filter register name…"
            }
            inputProps={{ "data-testid": "run-athena-logs-filter" }}
            sx={{ minWidth: 260 }}
          />
        </Box>

        {!canQuery && (
          <Typography data-testid="run-athena-logs-nodate" color="text.secondary">
            Run date is unavailable for this run, so its run logs cannot be
            queried.
          </Typography>
        )}

        {canQuery && isLoading && (
          <Box
            data-testid="run-athena-logs-loading"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <CircularProgress size={20} />
            <Typography color="text.secondary">Loading run logs…</Typography>
          </Box>
        )}

        {canQuery && isError && (
          <Typography data-testid="run-athena-logs-error" color="error">
            Failed to load run logs:{" "}
            {(error as Error)?.message ?? "unknown error"}
          </Typography>
        )}

        {canQuery && !isLoading && !isError && rows.length === 0 && (
          <Typography data-testid="run-athena-logs-empty" color="text.secondary">
            No {SUB_VIEWS.find((v) => v.key === table)?.label} data was ingested
            for this run.
          </Typography>
        )}

        {canQuery && !isLoading && !isError && rows.length > 0 && (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 640 }}
          >
            <Table stickyHeader size="small" data-testid="run-athena-logs-table">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col}
                      sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={idx} hover data-testid="run-athena-logs-row">
                    {columns.map((col) => {
                      const value = r[col];
                      if (col === "level" && value != null) {
                        return (
                          <TableCell key={col}>
                            <Chip
                              label={String(value)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell
                          key={col}
                          sx={{
                            fontFamily:
                              col === "message" ? "monospace" : undefined,
                            whiteSpace:
                              col === "message" ? "pre-wrap" : "nowrap",
                            verticalAlign: "top",
                          }}
                        >
                          {value == null ? "" : String(value)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }
);

export default RunAthenaLogs;
